import React from 'react';
import { useToastStore } from '../../../stores/ToastStore';

/**
 * Inlines border/padding/background styles on tables, th, and td so that
 * HTML pasted into rich-text targets (email, Docs, Word) keeps its structure.
 */
function inlineClipboardTableStyles(root: ParentNode) {
	const tables = root.querySelectorAll('table');
	tables.forEach((table) => {
		table.setAttribute('border', '1');
		table.setAttribute('cellpadding', '6');
		table.setAttribute('cellspacing', '0');
		table.style.borderCollapse = 'collapse';
		table.style.width = '100%';
		table.style.border = '1px solid #d1d5db';
	});

	const tableHeaders = root.querySelectorAll('th');
	tableHeaders.forEach((headerCell) => {
		headerCell.style.border = '1px solid #d1d5db';
		headerCell.style.padding = '6px 8px';
		headerCell.style.textAlign = 'left';
		headerCell.style.verticalAlign = 'top';
		headerCell.style.fontWeight = '600';
		headerCell.style.backgroundColor = '#f3f4f6';
	});

	const tableCells = root.querySelectorAll('td');
	tableCells.forEach((cell) => {
		cell.style.border = '1px solid #d1d5db';
		cell.style.padding = '6px 8px';
		cell.style.verticalAlign = 'top';
	});
}

function normalizeClipboardText(value: string) {
	return value.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function getClipboardHtml(sourceHtml: string) {
	if (!sourceHtml) return '';
	const wrapper = document.createElement('div');
	wrapper.innerHTML = sourceHtml;
	inlineClipboardTableStyles(wrapper);
	return wrapper.innerHTML.trim();
}

/**
 * Hook that encapsulates all clipboard operations for a message bubble.
 *
 * @param messageContentRef ref to the rendered message DOM node (used to read
 *   inner text / HTML for rich-clipboard support).
 */
export function useMessageClipboard(
	messageContentRef: React.RefObject<HTMLDivElement | null>,
) {
	const { showToast } = useToastStore();

	/** Copy the full message as rich text (HTML + plain text) when supported. */
	const handleCopy = async (messageContent: string) => {
		try {
			const container = messageContentRef.current;
			const plainText = normalizeClipboardText(container?.innerText || messageContent || '');
			const html = getClipboardHtml(container?.innerHTML?.trim() || '');

			const supportsRichClipboard =
				typeof window !== 'undefined' &&
				// eslint-disable-next-line compat/compat -- runtime feature-detection; falls back to writeText below when ClipboardItem is unavailable
				typeof window.ClipboardItem !== 'undefined' &&
				typeof navigator.clipboard?.write === 'function';

			if (supportsRichClipboard && html) {
				// eslint-disable-next-line compat/compat -- guarded by supportsRichClipboard feature-detection above
				const clipboardItem = new window.ClipboardItem({
					'text/plain': new Blob([plainText], {
						type: 'text/plain',
					}),
					'text/html': new Blob([html], { type: 'text/html' }),
				});
				await navigator.clipboard.write([clipboardItem]);
			} else {
				await navigator.clipboard.writeText(plainText);
			}

			showToast('Copied to clipboard', 'info', {
				timeout: 3000,
				placement: 'chat',
			});
		} catch (err) {
			console.error('Failed to copy message: ', err);
			showToast('Failed to copy message', 'error', {
				timeout: 3000,
				placement: 'chat',
			});
		}
	};

	/** Copy raw Markdown source to the clipboard. */
	const handleCopyAsMarkdown = async (markdownContent: string) => {
		try {
			await navigator.clipboard.writeText(markdownContent);
			showToast('Markdown copied to clipboard', 'info', {
				timeout: 3000,
				placement: 'chat',
			});
		} catch (err) {
			console.error('Failed to copy markdown: ', err);
			showToast('Failed to copy markdown', 'error', {
				timeout: 3000,
				placement: 'chat',
			});
		}
	};

	/** Intercepts the native copy event to supply both plain-text and styled HTML. */
	const handleCopyEvent = (e: React.ClipboardEvent<HTMLDivElement>) => {
		e.preventDefault();
		const selection = window.getSelection();
		const selectedText = normalizeClipboardText(selection ? selection.toString() : '');
		e.clipboardData.setData('text/plain', selectedText);

		if (selection && selection.rangeCount > 0) {
			const range = selection.getRangeAt(0);
			const fragment = range.cloneContents();
			const wrapper = document.createElement('div');
			wrapper.appendChild(fragment);
			const selectedHtml = getClipboardHtml(wrapper.innerHTML);

			if (selectedHtml) {
				e.clipboardData.setData('text/html', selectedHtml);
			}
		}
	};

	return { handleCopy, handleCopyAsMarkdown, handleCopyEvent };
}
